import { cloneElement, useEffect, forwardRef, useState, useRef, Children } from 'react';
import type { FC, ReactNode, ComponentType, ReactElement, PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';

import { defaultThemeProp } from '../../theme';
import type { Action, ExcludeStrict, ForwardProps, OmitStrict, TestIdProp } from '../../types';
import Flex, { StyledFlex } from '../Flex';
import type { FlexProps } from '../Flex';
import Actions from '../Actions';
import type { FormControlProps } from '../FormControl';
import { useConsolidatedRef, useI18n, useLiveLog, useTestIds, useTheme, useUID } from '../../hooks';
import Label, { StyledLabel } from '../Label';
import type { LabelProps } from '../Label';
import {
  calculateFontSize,
  getHoverColors,
  readableColor,
  getReadableStatusColor,
  type FontSize
} from '../../styles';
import VisuallyHiddenText from '../VisuallyHiddenText';
import { getFocusables, tryCatch, withTestIds, createClassName } from '../../utils';
import Button from '../Button';
import Icon, { registerIcon } from '../Icon';
import * as warnSolidIcon from '../Icon/icons/warn-solid.icon';
import * as diamondMinusIcon from '../Icon/icons/diamond-minus.icon';
import * as checkIcon from '../Icon/icons/check.icon';
import AdditionalInfo, { StyledAdditionalInfo } from '../AdditionalInfo';
import { StyledRadioCheck, StyledRadioCheckInput } from '../RadioCheck/RadioCheck';

import { getFormFieldTestIds } from './FormField.test-ids';
import type { elements } from './FormField.test-ids';

registerIcon(warnSolidIcon, diamondMinusIcon, checkIcon);

export interface FormFieldProps
  extends OmitStrict<FormControlProps, 'placeholder' | 'value' | 'defaultValue'>,
    TestIdProp<typeof elements> {
  /** Pass a single form control component i.e. Input | TextArea | Select | etc.. */
  children: ReactElement | ReactElement[];
  /**
   * An id is required to connect a FormField's wrapping element, control element and info(aria-describedby) live region.
   * A random id will be generated if none is provided.
   * Since MOST FormControls should possess an id(excl. i.e RadioCheckGroup and others) this can be used.
   * The id prop will be used to generate the following:
   * - FormControl <-> Label association via a Label's htmlFor prop.
   * - FormField's wrapping element id {id}-field.
   * - FormField info element id ${id}-info.
   */
  id?: string;
  /**
   * Determines how the wrapping label should be rendered.
   * @default 'label'
   */
  labelAs?: LabelProps['as'];
  /**
   * Overrides the for attribute on the label. If this is not set it will default to the forms id.
   * @default undefined
   */
  labelFor?: LabelProps['htmlFor'];
  /**
   * Sets the id of the label.
   * @default undefined
   */
  labelId?: LabelProps['id'];
  /**
   * Visually hides the label region.
   * @default false
   */
  labelHidden?: boolean;
  /**
   * Visually places the label after the input.
   * @default false
   */
  labelAfter?: boolean;
  /**
   * Property used to check whether input type is radio to handle styling for RadioCheck
   * @default false
   */
  isRadioCheck?: boolean;
  /**
   * Wrapping HTML element tag. Renders as a fieldset for grouped elements i.e. RadioButtons/CheckboxGroup
   * @default 'div'
   */
  as?: 'div' | 'fieldset' | ComponentType<any>;
  /**
   * Layout field elements inline in a row.
   * @default false
   */
  inline?: boolean;
  /** Optionally utilized by Input or Select. */
  actions?: Action[];
  /** Enables Flex container prop pass through. */
  container?: ExcludeStrict<FlexProps['container'], true>;
  /** Character remaining count. Typically used on Textareas only */
  charLimitDisplay?: ReactNode;
  /** Id of additional describing element. */
  'aria-describedby'?: string;
  /** Callback invoked when the clear button is clicked. If provided will render the clear button. */
  onClear?: () => void;
  /** Suppresses announcements for status changes. */
  suppressAnnouncements?: boolean;
}

const StyledStatusIcon = styled(Icon)<{ status: NonNullable<FormFieldProps['status']> }>(({
  theme,
  status
}) => {
  return css`
    height: 1em;
    width: 1em;
    color: ${getReadableStatusColor(theme, status)};
    vertical-align: baseline;
  `;
});

StyledStatusIcon.defaultProps = defaultThemeProp;

const StyledClearButton = styled(Button)(({ theme }) => {
  return css`
    margin-block-start: calc(${theme.base.spacing} / 2);
    align-self: start;
  `;
});

StyledClearButton.defaultProps = defaultThemeProp;

export const StyledFormFieldInfo = styled.div<
  Pick<FormControlProps, 'status'> & Pick<FormFieldProps, 'inline'> & { $infoAbove?: boolean }
>(({ status, inline, $infoAbove, theme }) => {
  const {
    base: { 'font-size': fontSize, 'font-scale': fontScale, spacing },
    components: { 'form-field': formField }
  } = theme;
  const fontSizes = calculateFontSize(fontSize, fontScale);
  const infoFontSize = fontSizes.xxs;
  const labelFontSize = fontSizes[theme.components.text.primary['font-size'] as FontSize];

  return css`
    max-width: max-content;
    font-size: ${infoFontSize};
    word-break: break-word;

    ${inline &&
    css`
      padding-inline-start: calc(${labelFontSize} / ${infoFontSize} * 14ch);
      min-width: 100%;
    `}

    &:not(:empty) {
      ${$infoAbove
        ? css`
            margin-block-end: calc(0.25 * ${spacing});
          `
        : css`
            margin-block-start: calc(0.25 * ${spacing});
          `}
    }

    ${status &&
    formField[status] &&
    css`
      color: ${getReadableStatusColor(theme, status)};
    `}
  `;
});

StyledFormFieldInfo.defaultProps = defaultThemeProp;

export const StyledLabelRow = styled.div<{ inline: boolean }>(({ inline }) => {
  if (inline) {
    return css`
      flex-shrink: 0;
      width: 14ch;
      ${StyledAdditionalInfo} {
        align-self: center;
      }
    `;
  }
});

export const StyledFormField = styled.div<
  FormFieldProps & { asFieldset: boolean; showAdditionalInfo: boolean }
>(props => {
  const {
    asFieldset,
    showAdditionalInfo,
    disabled,
    required,
    theme: {
      base: {
        palette: { urgent },
        'disabled-opacity': disabledOpacity,
        spacing,
        'hit-area': { 'compact-min': hitAreaCompact }
      }
    }
  } = props;

  return css`
    ${disabled &&
    css`
      opacity: ${disabledOpacity};
      -webkit-user-select: none;
      user-select: none;
    `}
    position: relative;
    border: 0;

    &:has(${StyledRadioCheckInput}:only-of-type) {
      ${StyledFormFieldInfo} {
        min-width: 100%;
      }
    }

    ${StyledFlex}:has(${StyledRadioCheck}) {
      flex-shrink: 0;
    }

    > ${StyledLabel}, > ${StyledLabelRow}, > ${StyledFlex}:has(> ${StyledLabel}) {
      &:not(:empty) {
        margin-bottom: calc(0.25 * ${spacing});
        min-height: ${hitAreaCompact};
      }
    }

    > ${StyledLabel}, > ${StyledLabelRow} > ${StyledLabel}, > ${StyledFlex} > ${StyledLabel} {
      ${required &&
      css`
        &::after {
          content: ${'"\\00a0*" / ""'};
          color: ${urgent};
        }
      `}

      ${disabled &&
      css`
        cursor: not-allowed;
      `}
    }

    ${asFieldset &&
    showAdditionalInfo &&
    css`
      > ${StyledFlex} > ${StyledLabel} {
        display: block;
      }

      > ${StyledFlex}:has(> ${StyledLabel}) > ${StyledAdditionalInfo} {
        align-self: flex-start;
      }
    `}
  `;
});

StyledFormField.defaultProps = defaultThemeProp;

const StyledSuggestionButton = styled(Button)(({ theme }) => {
  const {
    base: { 'font-size': fontSize, 'font-scale': fontScale, 'border-radius': baseRadius, spacing },
    components: {
      'form-control': { 'border-radius': radius, 'border-width': borderWidth }
    }
  } = theme;
  const { xxs: buttonFontSize } = calculateFontSize(fontSize, fontScale);
  const backgroundColor = getReadableStatusColor(theme, 'pending');
  const hoverColors = getHoverColors(backgroundColor);
  const color = tryCatch(() => readableColor(backgroundColor));
  const borderColor = color ? rgba(color, 0.4) : color;

  return css`
    background-color: ${backgroundColor};
    color: ${color};
    font-size: ${buttonFontSize};
    min-width: calc(3 * ${spacing});
    min-height: calc(3 * ${spacing});
    padding: 0 ${spacing};
    border-radius: 0;
    border: none;

    &:first-child {
      border-inline-end: ${borderWidth} solid ${borderColor};
      border-end-start-radius: calc(${baseRadius} * ${radius});
      margin-inline-start: calc(2 * ${spacing});
    }

    &:last-child {
      border-end-end-radius: calc(${baseRadius} * ${radius});
      margin-inline-start: 0;
    }

    &:hover {
      color: ${hoverColors.foreground};
      background-color: ${hoverColors.background};
    }

    @media (pointer: coarse) {
      min-height: 2rem;
    }
  `;
});

StyledSuggestionButton.defaultProps = defaultThemeProp;

const statusIconMap = { error: 'warn-solid', warning: 'diamond-minus', success: 'check' };

const FormField: FC<FormFieldProps & ForwardProps> = forwardRef(function FormField(
  props: PropsWithoutRef<FormFieldProps>,
  ref: Ref<HTMLElement>
) {
  const uid = useUID();
  const theme = useTheme();
  const {
    testId,
    children: controlElement,
    id = uid,
    as = 'div',
    label,
    labelAs: labelAsProp = 'label',
    labelFor = id,
    labelId: labelIdProp,
    labelHidden = false,
    labelAfter = false,
    info,
    status,
    suppressAnnouncements,
    isRadioCheck,
    charLimitDisplay,
    required = false,
    disabled = false,
    readOnly = false,
    inline = theme.components['form-field'].layout === 'inline',
    onClear,
    actions,
    container,
    additionalInfo,
    onResolveSuggestion,
    'aria-describedby': ariaDescribedBy,
    className,
    ...restProps
  } = props;

  const infoAbove = theme.components['form-field']['helper-text-position'] === 'above';
  const testIds = useTestIds(testId, getFormFieldTestIds);

  const asFieldset =
    as === 'fieldset' || (typeof as !== 'string' && 'target' in as && as.target === 'fieldset');
  const t = useI18n();
  const { announceAssertive } = useLiveLog();
  const labelAs = asFieldset ? 'span' : labelAsProp;
  const hasSuggestion = status === 'pending' && !!onResolveSuggestion;
  const consolidatedRef = useConsolidatedRef(ref);
  const labelRef = useRef<HTMLLabelElement | HTMLLegendElement>(null);
  const [labelText, setLabelText] = useState<string | null>(null);

  const showAdditionalInfo = !!additionalInfo && !disabled && !labelHidden;
  const labelInline = inline && !isRadioCheck;
  const labelId = asFieldset && !labelIdProp ? `${id}-label` : labelIdProp;

  const labelContent = (
    <>
      <Label
        data-testid={testIds.label}
        id={labelId}
        as={labelAs}
        htmlFor={labelAs === 'label' ? labelFor : undefined}
        labelHidden={labelHidden}
        onClick={(e: MouseEvent) => {
          if (readOnly) e.preventDefault();
        }}
        inline={inline}
        ref={labelRef}
      >
        {label}
      </Label>

      {showAdditionalInfo && labelText && (
        <AdditionalInfo
          data-testid={testIds.additionalInfo}
          heading={additionalInfo.heading}
          contextualLabel={labelText}
        >
          {additionalInfo.content}
        </AdditionalInfo>
      )}
    </>
  );

  let labelRow: ReactNode;
  if (!isRadioCheck && !asFieldset && !labelHidden) {
    labelRow = (
      <Flex
        as={StyledLabelRow}
        container={{ justify: labelInline ? 'start' : 'between', alignItems: 'end' }}
        item={{ alignSelf: 'stretch', shrink: labelInline ? 0 : undefined }}
        inline={labelInline}
      >
        {labelContent}
      </Flex>
    );
  } else if (asFieldset && !labelHidden) {
    labelRow = (
      <Flex
        container={{
          alignItems: 'end',
          justify: showAdditionalInfo && !isRadioCheck ? 'between' : undefined
        }}
      >
        {labelContent}
      </Flex>
    );
  } else {
    labelRow = labelContent;
  }

  useEffect(() => {
    setLabelText(labelRef.current?.textContent ?? null);
  }, [label]);

  useEffect(() => {
    if (suppressAnnouncements || (status !== 'error' && status !== 'warning')) return;

    let message = t(status);

    if (typeof info === 'string') message = `${message} ${info}`;

    if (labelRef.current?.textContent) message = `${labelRef.current?.textContent} ${message}`;

    announceAssertive({ message, type: status });
  }, [status, info]);

  // fieldset or single form control element
  let content: FormFieldProps['children'];
  if (asFieldset || Children.count(controlElement) > 1) {
    content = controlElement;
  } else {
    const controlElementAriaDescribedBy = Children.only(controlElement).props[
      'aria-describedby'
    ] as string | undefined;
    content = cloneElement(controlElement as ReactElement, {
      'aria-describedby':
        [controlElementAriaDescribedBy, info && !readOnly ? `${id}-info` : undefined]
          .join(' ')
          .trim() || undefined
    });
  }

  if (actions) {
    content = (
      <Flex
        container={{ alignItems: 'center', gap: 0.5 }}
        item={{ grow: labelInline ? 1 : undefined }}
      >
        {content}
        <Actions items={actions} menuAt={3} />
      </Flex>
    );
  }

  let infoContent = (
    <Flex
      id={`${id}-info`}
      data-testid={testIds.info}
      status={status}
      as={StyledFormFieldInfo}
      container={{ alignItems: 'center', gap: 0.5 }}
      inline={labelInline}
      $infoAbove={infoAbove}
    >
      {status && status !== 'pending' && (
        <>
          <Flex
            item={{ alignSelf: 'start' }}
            as={StyledStatusIcon}
            status={status}
            name={statusIconMap[status]}
          />
          <VisuallyHiddenText>{`${t(status)} `}</VisuallyHiddenText>
        </>
      )}
      {info}
    </Flex>
  );

  if (charLimitDisplay) {
    infoContent = (
      <Flex container={{ justify: infoContent ? 'between' : 'end', gap: 1 }}>
        {infoContent}
        <Flex item={{ shrink: 0 }}>{charLimitDisplay}</Flex>
      </Flex>
    );
  }

  if (hasSuggestion) {
    const focusables = getFocusables(consolidatedRef);
    infoContent = (
      <Flex container={{ alignItems: 'start', justify: 'between' }}>
        <StyledFormFieldInfo
          data-testid={testIds.info}
          status={status}
          id={`${id}-info`}
          $infoAbove={infoAbove}
        >
          {t('suggestion_info')}
          <VisuallyHiddenText aria-live='polite'>{t('suggestion_assist')}</VisuallyHiddenText>
        </StyledFormFieldInfo>
        <Flex container={{ wrap: 'nowrap' }}>
          <StyledSuggestionButton
            data-testid={testIds.suggestionReject}
            onClick={() => {
              onResolveSuggestion(false);
              focusables[0]?.focus();
            }}
            aria-label={`${t('no')}, ${t('reject_suggestion_button_a11y')}${labelText ? ` - ${labelText}` : ''}`}
          >
            {t('no')}
          </StyledSuggestionButton>
          <StyledSuggestionButton
            data-testid={testIds.suggestionAccept}
            onClick={() => {
              onResolveSuggestion(true);
              focusables[0]?.focus();
            }}
            aria-label={`${t('yes')}, ${t('accept_suggestion_button_a11y')}${labelText ? ` - ${labelText}` : ''}`}
          >
            {t('yes')}
          </StyledSuggestionButton>
        </Flex>
      </Flex>
    );
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLElement;
      if (!target.closest('button')) {
        e.preventDefault();
        onResolveSuggestion?.(true);
      }
    }
  };

  const descriptionIds = [];
  if (ariaDescribedBy) descriptionIds.push(ariaDescribedBy);
  if (asFieldset && info) descriptionIds.push(`${id}-info`);

  return (
    <Flex
      data-testid={testIds.root}
      {...restProps}
      container={{
        direction: inline ? 'row' : 'column',
        alignItems: inline ? 'center' : undefined,
        wrap: isRadioCheck || inline ? 'wrap' : undefined,
        ...container
      }}
      as={StyledFormField}
      asFieldset={asFieldset}
      isRadioCheck={isRadioCheck}
      showAdditionalInfo={showAdditionalInfo}
      id={`${id}-field`}
      forwardedAs={as}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      onKeyDown={hasSuggestion ? onKeyDown : undefined}
      aria-describedby={descriptionIds.length ? descriptionIds.join(' ') : undefined}
      aria-labelledby={asFieldset ? labelId : undefined}
      ref={consolidatedRef}
      className={createClassName('form-field', className, { status })}
    >
      {(asFieldset || !labelAfter) && labelRow}
      {!readOnly && infoAbove && infoContent}
      {content}
      {!asFieldset && labelAfter && labelRow}
      {!readOnly && !infoAbove && infoContent}
      {isRadioCheck && onClear && (
        <StyledClearButton
          variant='link'
          onClick={() => {
            onClear();
          }}
        >
          {t('clear_selection')}
        </StyledClearButton>
      )}
    </Flex>
  );
});

export default withTestIds(FormField, getFormFieldTestIds);
